import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, X } from "@phosphor-icons/react";

/**
 * Photo / video picker with preview and upload to backend.
 * - `accept`: MIME pattern (default "image/*")
 * - `multiple`: enable multi-pick (returns array of file_ids)
 * - `publicUpload`: use the public endpoint (admission form), defaults to false
 * - onUploaded(fileIdsOrId) called when uploads complete
 */
export default function PhotoCapture({
  accept = "image/*",
  multiple = false,
  publicUpload = false,
  label = "Add a photo",
  capture,
  onUploaded,
  testid = "photo-capture",
}) {
  const inputRef = useRef(null);
  const [previews, setPreviews] = useState([]);  // [{url, file_id, type}]
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const upload = async (files) => {
    setUploading(true);
    const out = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append("file", file);
        const endpoint = publicUpload
          ? "/files/upload-public?folder=admission"
          : "/files/upload?folder=daily_updates";
        const { data } = await api.post(endpoint, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        out.push({ url: URL.createObjectURL(file), file_id: data.file_id, type: data.content_type });
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setPreviews((p) => [...p, ...out]);
    const ids = [...previews, ...out].map((x) => x.file_id);
    onUploaded?.(multiple ? ids : ids[0] || null);
    setUploading(false);
  };

  const onChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) upload(files);
    e.target.value = ""; // allow re-pick same file
  };

  const remove = (idx) => {
    const next = previews.filter((_, i) => i !== idx);
    setPreviews(next);
    const ids = next.map((x) => x.file_id);
    onUploaded?.(multiple ? ids : ids[0] || null);
  };

  return (
    <div data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        capture={capture}
        multiple={multiple}
        onChange={onChange}
        className="hidden"
      />
      {previews.length === 0 ? (
        <button
          type="button"
          onClick={pick}
          disabled={uploading}
          className="w-full h-32 rounded-2xl border-2 border-dashed border-border bg-paper-alt hover:bg-paper hover:border-terracotta transition flex flex-col items-center justify-center gap-2 text-ink-soft disabled:opacity-60"
          data-testid={`${testid}-trigger`}
        >
          {capture === "user" ? <Camera weight="duotone" size={28} /> : <ImageIcon weight="duotone" size={28} />}
          <span className="text-sm font-medium">{uploading ? "Uploading..." : label}</span>
        </button>
      ) : (
        <div>
          <div className={`grid ${multiple ? "grid-cols-3" : "grid-cols-1"} gap-2`}>
            {previews.map((p, i) => (
              <div key={p.file_id} className="relative group">
                {p.type?.startsWith("video/") ? (
                  <video src={p.url} className="w-full h-32 rounded-2xl object-cover bg-paper-alt" controls />
                ) : (
                  <img src={p.url} alt="" className="w-full h-32 rounded-2xl object-cover bg-paper-alt" />
                )}
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-90 hover:bg-black"
                  data-testid={`${testid}-remove-${i}`}
                >
                  <X weight="bold" size={14} />
                </button>
              </div>
            ))}
            {multiple && (
              <button
                type="button"
                onClick={pick}
                disabled={uploading}
                className="h-32 rounded-2xl border-2 border-dashed border-border bg-paper-alt hover:bg-paper flex items-center justify-center text-ink-soft disabled:opacity-60"
                data-testid={`${testid}-add-more`}
              >
                + Add
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
